import React__default from 'react';
import { toast } from '@grandvisionhq/common';
import { useConfiguratorContext } from '@grandvisionhq/configurator';
import { useDispatch, useSelector, useFeatureToggles, statusSuccess } from '@grandvisionhq/state';
import { useUpload, useUploadHandler, Types } from '@grandvisionhq/upload';
import { useGetPrescriptionValues } from '../../../../hooks/use-get-prescription-values.js';
import '../../../../state/actions.js';
import { PRESCRIPTION_STEP_UPLOAD_ERROR_ID, RX_SCOPE, PD_SCOPE } from '../../../../state/constants.js';
import '../../../../state/add-to-cart/selectors.js';
import '../../../../state/bundles/selectors.js';
import '../../../../state/bundles/reducer.js';
import { getSelectedFrame, getPrescription, getPrescriptionType, getPdUploadValidationRequirement } from '../../../../state/configurator/selectors.js';
import '../../../../state/configurator/reducer.js';
import '../../../../state/products/selectors.js';
import '../../../../state/products/reducer.js';
import { isNewPrescriptionAndUpload } from '../../../../utils/is-new-prescription-upload.js';
import { PrescriptionUploadForm } from './PrescriptionUploadForm.js';
import { fileUploadResult } from '../../../../state/data-layer/actions.js';
import { updateUploadReference, removeUploadReference } from '../../../../state/configurations/actions.js';

const PrescriptionUploadFormContainer = ({ className  })=>{
    const dispatch = useDispatch();
    const { goToNextStep  } = useConfiguratorContext();
    const frame = useSelector(getSelectedFrame);
    const { isPrescriptionRecognitionEnabled  } = useFeatureToggles();
    const prescription = useSelector(getPrescription);
    const prescriptionType = useSelector(getPrescriptionType);
    const pdUploadValidationRequirement = useSelector(getPdUploadValidationRequirement);
    const { scopes , setError , configs  } = useUpload();
    const { lastError , state , handlers , checkMissingFiles  } = useUploadHandler({
        sharedErrorElementId: PRESCRIPTION_STEP_UPLOAD_ERROR_ID,
        configs: scopes.map((scope)=>{
            var ref;
            const ref1 = statusSuccess(prescription) ? (ref = prescription.result.uploadReferences) === null || ref === void 0 ? void 0 : ref[scope] : undefined;
            const isPdUploadRequired = Boolean(pdUploadValidationRequirement === null || pdUploadValidationRequirement === void 0 ? void 0 : pdUploadValidationRequirement.isRequired);
            const required = scope === PD_SCOPE ? isPdUploadRequired : true;
            return {
                scope,
                required,
                uploadStatus: ref1 ? Types.uploadStatusSuccess(ref1) : undefined
            };
        })
    });
    const [fetchRxRecognitionValues] = useGetPrescriptionValues(frame === null || frame === void 0 ? void 0 : frame.sku);
    React__default.useEffect(()=>{
        var ref, ref2;
        if (((ref = configs[RX_SCOPE]) === null || ref === void 0 ? void 0 : ref.status) !== 'success') {
            dispatch(fileUploadResult('upload not possible - rx config'));
        }
        if (((ref2 = configs[PD_SCOPE]) === null || ref2 === void 0 ? void 0 : ref2.status) !== 'success') {
            dispatch(fileUploadResult('upload not possible - pd config'));
        }
    }, [
        configs,
        dispatch
    ]);
    React__default.useEffect(()=>{
        setError(lastError);
    // Adding setError in the deps array creates an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        lastError
    ]);
    const handleNextStepClick = React__default.useCallback((e)=>{
        e === null || e === void 0 ? void 0 : e.preventDefault();
        goToNextStep();
        /**
       * checkMissingFiles only updates the UI.
       * If the form is valid in state and goToNextStep is successful,
       * then it doesn't matter if checkMissingFiles runs or not
       */ const hasMissing = checkMissingFiles();
        if (hasMissing) {
            const isPDMissing = !Types.isUploadSuccess(state[PD_SCOPE]);
            const isRXMissing = !Types.isUploadSuccess(state[RX_SCOPE]) && (pdUploadValidationRequirement === null || pdUploadValidationRequirement === void 0 ? void 0 : pdUploadValidationRequirement.isRequired);
            if (!isPDMissing && !isRXMissing) return;
            if (isPDMissing && isRXMissing) {
                dispatch(fileUploadResult('files missing'));
            } else {
                dispatch(fileUploadResult(isRXMissing ? 'rx missing' : 'pd missing'));
            }
        }
    }, [
        goToNextStep,
        checkMissingFiles,
        state,
        dispatch,
        pdUploadValidationRequirement === null || pdUploadValidationRequirement === void 0 ? void 0 : pdUploadValidationRequirement.isRequired
    ]);
    const handleStatusChange = React__default.useCallback((callbackStatus, scope)=>{
        if (!frame) {
            return;
        }
        if (Types.isUploadSuccess(callbackStatus)) {
            toast.success('products.glasses.upload.success.message');
            dispatch(fileUploadResult(scope === PD_SCOPE ? 'pd success' : 'rx success'));
            if (isPrescriptionRecognitionEnabled && scope === RX_SCOPE && !isNewPrescriptionAndUpload(prescriptionType)) {
                const { storageId , storageToken  } = callbackStatus.uploadReference;
                fetchRxRecognitionValues(storageId, storageToken);
            }
            dispatch(updateUploadReference({
                frameSku: frame.sku,
                scope,
                uploadReference: callbackStatus.uploadReference
            }));
        }
        const removeRxValues = !isNewPrescriptionAndUpload(prescriptionType);
        /**
       * User deleted the file, so we want to remove our reference to it.
       */ if (Types.isUploadIdleAfterFileRemove(callbackStatus)) {
            dispatch(fileUploadResult(scope === PD_SCOPE ? 'pd remove' : 'rx remove'));
            dispatch(removeUploadReference({
                frameSku: frame.sku,
                scope,
                removeRxValues
            }));
        }
        /**
       * User tried to overwrite a file, but it failed. Since the user intended to overwrite the
       * existing file and we show in the UI that the upload has failed, we remove the existing file
       * reference we had to not confuse the user. Worst case scenario they re-upload their previous file.
       */ if (Types.isUploadError(callbackStatus)) {
            dispatch(fileUploadResult(callbackStatus.error));
            dispatch(removeUploadReference({
                frameSku: frame.sku,
                scope,
                removeRxValues
            }));
        }
        return handlers[scope](callbackStatus);
    }, [
        frame,
        handlers,
        dispatch,
        isPrescriptionRecognitionEnabled,
        fetchRxRecognitionValues
    ]);
    if (!statusSuccess(prescription) || !frame) {
        // error states are handled further up the tree
        return null;
    }
    return /*#__PURE__*/ React__default.createElement(PrescriptionUploadForm, {
        className: className,
        onSubmit: handleNextStepClick,
        onStatusChange: handleStatusChange,
        uploadState: state,
        showPdUpload: pdUploadValidationRequirement === null || pdUploadValidationRequirement === void 0 ? void 0 : pdUploadValidationRequirement.isRequired
    });
};

export { PrescriptionUploadFormContainer };
//# sourceMappingURL=PrescriptionUploadFormContainer.js.map
